import { queryClient } from 'configs/client';
import { LocalStorageKey } from 'configs/constants';
import { ECookieVariable } from 'configs/cookies';
import { Roles } from 'configs/roles';
import { AuthRouteKeys } from 'routes/enums';
import { CookieService } from 'services/cookie';
import { ProfileUserEntity, UserWalletAuthService } from 'services/openapi';

export const shouldRefresh = (_token: string) => false;

export const shouldLogout = (token: string) => !token;

export const getUserRoles = () => {
  // JWT claims are not available in the browser in the HttpOnly-cookie flow.
  // Use a non-sensitive role field from the profile when the API provides it;
  // authorization is still enforced by the API.
  try {
    const userInfo = JSON.parse(
      sessionStorage.getItem('promax_admin_user_info') || '{}',
    );
    const roles = userInfo?.roles || userInfo?.role;
    if (Array.isArray(roles)) return roles as Roles[];
    if (roles) return [roles as Roles];
  } catch (error) {
    return [];
  }
  return [];
};

export type UserAccess = {
  roles: Roles[];
  adminActions: string[];
};

export const checkValidRole = (
  roles: string | Roles[],
  userRoles: Roles[] = getUserRoles(),
) => {
  if (typeof roles === 'string' && roles === '*') {
    return true;
  } else if (Array.isArray(roles) && roles.length > 0 && userRoles.length > 0) {
    const isValid = roles.some((r) => {
      return userRoles.indexOf(r) >= 0;
    });
    return isValid;
  } else if (typeof roles === 'string' && userRoles.length > 0) {
    return userRoles.indexOf(roles as Roles) >= 0;
  }
  return false;
};

export const checkValidActionRoleAdmin = (
  screenAction?: string | string[],
  access?: UserAccess,
) => {
  if (!screenAction) {
    return true;
  }
  const userRoles = access?.roles ?? getUserRoles();
  if (!userRoles.includes(Roles.ADMIN)) {
    return true;
  } else {
    const adminActions =
      access?.adminActions ??
      localStorage.getItem(LocalStorageKey.ADMIN_ACTIONS);
    if (typeof screenAction === 'string') {
      return adminActions?.includes(screenAction) || false;
    } else if (Array.isArray(screenAction)) {
      return (
        screenAction.some((action) => adminActions?.includes(action)) || false
      );
    }
    return false;
  }
};

export const checkRolesIncludeRoles = (
  roles: string | Roles[],
  allRoles: string | Roles[],
) => {
  if (allRoles.length > 0 && typeof roles === 'string' && roles === '*') {
    return true;
  } else if (Array.isArray(roles) && roles.length > 0 && allRoles.length > 0) {
    const isValid = roles.some((r) => {
      return allRoles.indexOf(r) >= 0;
    });
    return isValid;
  } else {
    return false;
  }
};

export function getCsrfToken() {
  return CookieService.get(ECookieVariable.CSRF_TOKEN_KEY) || '';
}

let cachedProfile: ProfileUserEntity | null = null;
let profileRequest: Promise<ProfileUserEntity> | null = null;
let profileVersion = 0;

export function getAuthenticatedProfile(): Promise<ProfileUserEntity> {
  if (cachedProfile) {
    return Promise.resolve(cachedProfile);
  }

  if (!profileRequest) {
    const requestVersion = profileVersion;
    profileRequest = UserWalletAuthService.authControllerGetProfileUser()
      .then((profile) => {
        if (requestVersion !== profileVersion) {
          throw new Error('Session changed while loading profile');
        }
        cachedProfile = profile;
        setUserInfo(profile);
        return profile;
      })
      .finally(() => {
        if (requestVersion === profileVersion) profileRequest = null;
      });
  }

  return profileRequest;
}

export function clearAuthenticatedProfile() {
  profileVersion += 1;
  cachedProfile = null;
  profileRequest = null;
}

export async function hasAuthenticatedSession(): Promise<boolean> {
  try {
    await getAuthenticatedProfile();
    return true;
  } catch (error) {
    return false;
  }
}

export const handleLogin = () => {
  clearAuthenticatedProfile();
  queryClient.clear();
  CookieService.remove(ECookieVariable.CSRF_TOKEN_KEY);

  window.location.href = AuthRouteKeys.Root;
};

export const handleLogout = () => {
  clearAuthenticatedProfile();
  queryClient.clear();
  CookieService.remove(ECookieVariable.ACCESS_TOKEN_KEY);
  CookieService.remove(ECookieVariable.CSRF_TOKEN_KEY);
  sessionStorage.removeItem('promax_admin_user_info');
  // Notify other tabs before clearing storage so the storage event fires
  localStorage.setItem(LocalStorageKey.SESSION_MARKER, 'logout_' + Date.now());
  localStorage.clear();
  localStorage.removeItem('theme');
  document.documentElement.setAttribute('data-theme', 'dark-black');
  window.location.href = AuthRouteKeys.Root;
};

export const setUserInfo = (userInfo: unknown) => {
  sessionStorage.setItem('promax_admin_user_info', JSON.stringify(userInfo));
};

export const getUserInfo = () => {
  try {
    return JSON.parse(sessionStorage.getItem('promax_admin_user_info') || '{}');
  } catch (error) {
    return {};
  }
};
